import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Search,
  RefreshCw,
  CheckCheck,
  Mail,
  Building2,
  Clock,
  Inbox,
  Frown,
  Meh,
  Smile,
  Laugh,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useHeaderStore } from "../../store/headerStore";
import {
  submitFeedback,
  listFeedback,
  updateFeedbackStatus,
  markAllFeedbackRead,
} from "../../api/feedbackApi";
import type {
  FeedbackItem,
  FeedbackRating,
  FeedbackStatus,
} from "../../api/feedbackApi";
import FeedbackDetailModal from "../../components/FeedbackDetailModal";
import "../../styles/feedback-page.css";

// SVG Face Icons tailored to match the screenshot precisely
function BadFaceIcon() {
  return (
    <svg className="rating-icon-svg" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="19" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="16" cy="17" r="2.2" fill="currentColor" />
      <circle cx="28" cy="17" r="2.2" fill="currentColor" />
      <path d="M15 28C17 24.5 27 24.5 29 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function OkayFaceIcon() {
  return (
    <svg className="rating-icon-svg" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="19" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="16" cy="17" r="2.2" fill="currentColor" />
      <circle cx="28" cy="17" r="2.2" fill="currentColor" />
      <path d="M16 27H28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function GoodFaceIcon() {
  return (
    <svg className="rating-icon-svg" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="19" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="16" cy="17" r="2.2" fill="currentColor" />
      <circle cx="28" cy="17" r="2.2" fill="currentColor" />
      <path d="M15 25C17 28.5 27 28.5 29 25" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function AmazingFaceIcon() {
  return (
    <svg className="rating-icon-svg" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="19" stroke="currentColor" strokeWidth="2.5" />
      <path d="M13 18C14.5 15.5 17.5 15.5 19 18" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" />
      <path d="M25 18C26.5 15.5 29.5 15.5 31 18" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" />
      <path d="M14 24C16.5 29.5 27.5 29.5 30 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function getMiniRatingIcon(rating: FeedbackRating) {
  switch (rating) {
    case "bad":
      return <Frown size={13} />;
    case "okay":
      return <Meh size={13} />;
    case "good":
      return <Smile size={13} />;
    case "amazing":
      return <Laugh size={13} />;
    default:
      return <Smile size={13} />;
  }
}

function getDeterministicColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 65%, 45%)`;
}

function formatTimeAgo(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSec < 60) return "Just now";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDays = Math.floor(diffHour / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "";
  }
}

const RATING_CONFIG: Array<{
  id: FeedbackRating;
  label: string;
  className: string;
  icon: React.FC;
}> = [
  { id: "bad", label: "Bad", className: "rating-bad", icon: BadFaceIcon },
  { id: "okay", label: "Okay", className: "rating-okay", icon: OkayFaceIcon },
  { id: "good", label: "Good", className: "rating-good", icon: GoodFaceIcon },
  { id: "amazing", label: "Amazing", className: "rating-amazing", icon: AmazingFaceIcon },
];

export default function FeedbackPage() {
  const { user } = useAuth();
  const { setPageTitleOverride } = useHeaderStore();

  const role = (user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "super_admin" || role === "analyst";

  // Form states
  const [selectedRating, setSelectedRating] = useState<FeedbackRating | null>(null);
  const [suggestions, setSuggestions] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);

  // Admin section states
  const [adminFeedbacks, setAdminFeedbacks] = useState<FeedbackItem[]>([]);
  const [loadingAdminFeedbacks, setLoadingAdminFeedbacks] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | FeedbackStatus>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDetailFeedback, setActiveDetailFeedback] = useState<FeedbackItem | null>(null);

  useEffect(() => {
    setPageTitleOverride("FEEDBACK");
    return () => setPageTitleOverride(null);
  }, [setPageTitleOverride]);

  // Load admin client feedbacks
  const fetchAdminFeedbacks = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoadingAdminFeedbacks(true);
      const res = await listFeedback({ limit: 100 });
      setAdminFeedbacks(res.items);
    } catch (err) {
      console.error("Failed to load client feedback:", err);
    } finally {
      setLoadingAdminFeedbacks(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminFeedbacks();
    }
  }, [isAdmin, fetchAdminFeedbacks]);

  // Handle submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRating) {
      toast.error("Please select a rating before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback({
        rating: selectedRating,
        suggestions: suggestions.trim(),
      });

      toast.success("Thank you for your valuable feedback! It has been submitted directly to our team.");
      setSubmittedSuccess(true);
      setSuggestions("");

      // Refresh admin list if user is admin
      if (isAdmin) {
        fetchAdminFeedbacks();
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Failed to submit feedback. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedRating(null);
    setSuggestions("");
    setSubmittedSuccess(false);
  };

  // Status change handler
  const handleStatusChange = async (id: string, newStatus: FeedbackStatus) => {
    try {
      const res = await updateFeedbackStatus(id, newStatus);
      setAdminFeedbacks((prev) =>
        prev.map((f) => (f.id === id ? res.data : f))
      );
      if (activeDetailFeedback?.id === id) {
        setActiveDetailFeedback(res.data);
      }
      toast.success(`Feedback status updated to ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  // Open detail modal
  const handleOpenDetail = (item: FeedbackItem) => {
    setActiveDetailFeedback(item);
    if (item.status === "unread") {
      handleStatusChange(item.id, "reviewed");
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      const res = await markAllFeedbackRead();
      toast.success(`Marked ${res.modified_count} feedback(s) as reviewed`);
      setAdminFeedbacks((prev) =>
        prev.map((item) =>
          item.status === "unread" ? { ...item, status: "reviewed" } : item
        )
      );
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  // Filtered feedbacks
  const filteredFeedbacks = useMemo(() => {
    return adminFeedbacks.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = (item.user_name || "").toLowerCase().includes(q);
        const matchesEmail = (item.user_email || "").toLowerCase().includes(q);
        const matchesOrg = (item.user_organization || "").toLowerCase().includes(q);
        const matchesSuggestions = (item.suggestions || "").toLowerCase().includes(q);
        const matchesRating = (item.rating || "").toLowerCase().includes(q);
        return matchesName || matchesEmail || matchesOrg || matchesSuggestions || matchesRating;
      }
      return true;
    });
  }, [adminFeedbacks, statusFilter, searchQuery]);

  const unreadCount = useMemo(() => {
    return adminFeedbacks.filter((x) => x.status === "unread").length;
  }, [adminFeedbacks]);

  return (
    <div className="feedback-page-container">
      {/* Top Header */}
      <div className="feedback-page-header">
        <h1 className="feedback-page-title">Feedback</h1>
      </div>

      {/* Main Feedback Form Card */}
      <div className="feedback-card-wrapper">
        <div className="feedback-card">
          <form onSubmit={handleSubmit}>
            {/* Question Heading */}
            <h2 className="feedback-question-title">
              How would you like to rate Threatactix?
            </h2>

            {/* 4 Pastel Rating Cards */}
            <div className="feedback-ratings-grid" role="radiogroup" aria-label="Product rating">
              {RATING_CONFIG.map(({ id, label, className, icon: IconComponent }) => {
                const isSelected = selectedRating === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => {
                      setSelectedRating(id);
                      if (submittedSuccess) setSubmittedSuccess(false);
                    }}
                    className={`rating-option-btn ${className} ${isSelected ? "is-selected" : ""}`}
                  >
                    <div className="rating-icon-wrap">
                      <IconComponent />
                    </div>
                    <span className="rating-option-label">{label}</span>
                    {isSelected && (
                      <span className="selected-check-indicator" aria-hidden="true">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Suggestions / Feedback Textarea */}
            <div className="feedback-field-group">
              <label htmlFor="suggestions-input" className="feedback-field-label">
                Suggestions / Feedback
              </label>
              <textarea
                id="suggestions-input"
                className="feedback-textarea"
                rows={5}
                value={suggestions}
                onChange={(e) => {
                  setSuggestions(e.target.value);
                  if (submittedSuccess) setSubmittedSuccess(false);
                }}
                placeholder="Please give your valuable feedback"
                maxLength={5000}
              />
              <div className="feedback-character-count">
                {suggestions.length > 0 && `${suggestions.length} / 5000 characters`}
              </div>
            </div>

            {/* Footer Row: Support Link and Submit Button */}
            <div className="feedback-footer-row">
              <div className="feedback-help-link-wrap">
                <span>Need any help?</span>
                <Link to="/subscription" className="feedback-help-link">
                  Contact Support <ChevronRight size={14} />
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting || !selectedRating}
                className="feedback-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit</span>
                )}
              </button>
            </div>
          </form>

          {/* Success banner after submission */}
          {submittedSuccess && (
            <div className="feedback-success-banner">
              <div className="feedback-success-content">
                <CheckCircle2 size={18} />
                <span>Feedback saved to database successfully. Thank you for your review!</span>
              </div>
              <button
                type="button"
                className="feedback-success-dismiss"
                onClick={handleReset}
              >
                Submit another
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Admin Client Feedback Section (Visible only to Admin/Analyst) */}
      {isAdmin && (
        <div className="admin-feedback-section">
          {/* Header */}
          <div className="admin-feedback-section-header">
            <div className="admin-feedback-title-wrap">
              <h2 className="admin-feedback-heading">Client Submissions & Feedback</h2>
              <span className="admin-feedback-count-badge">
                {adminFeedbacks.length} Total
              </span>
              {unreadCount > 0 && (
                <span className="admin-feedback-unread-badge">
                  {unreadCount} New
                </span>
              )}
            </div>

            <div className="admin-feedback-header-actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="admin-btn-action"
                  onClick={handleMarkAllRead}
                  title="Mark all as reviewed"
                >
                  <CheckCheck size={14} />
                  <span>Mark all read</span>
                </button>
              )}
              <button
                type="button"
                className="admin-btn-action"
                onClick={fetchAdminFeedbacks}
                title="Refresh submissions"
              >
                <RefreshCw
                  size={13}
                  className={loadingAdminFeedbacks ? "animate-spin" : ""}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Toolbar: Search & Filter Tabs */}
          <div className="admin-feedback-toolbar">
            <div className="admin-feedback-search-wrap">
              <Search size={14} className="admin-feedback-search-icon" />
              <input
                type="text"
                className="admin-feedback-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by client name, email, organization, or feedback..."
              />
            </div>

            <div className="admin-feedback-status-tabs" role="tablist">
              {(["all", "unread", "reviewed", "archived"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === tab}
                  className={`admin-feedback-tab-btn ${
                    statusFilter === tab ? "is-active" : ""
                  }`}
                  onClick={() => setStatusFilter(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Feedback Items Feed */}
          <div className="admin-feedback-list">
            {loadingAdminFeedbacks ? (
              <div className="feedback-empty-state">
                <Loader2 size={24} className="animate-spin text-indigo-400" />
                <span>Loading client feedback submissions...</span>
              </div>
            ) : filteredFeedbacks.length === 0 ? (
              <div className="feedback-empty-state">
                <Inbox size={28} />
                <span>No client feedback found matching this filter</span>
              </div>
            ) : (
              filteredFeedbacks.map((item) => {
                const avatarColor = getDeterministicColor(
                  item.user_name || item.user_email || "User"
                );
                const initial = (
                  item.user_name ||
                  item.user_email ||
                  "?"
                )
                  .charAt(0)
                  .toUpperCase();

                return (
                  <div
                    key={item.id}
                    className={`admin-feedback-card-item ${
                      item.status === "unread" ? "is-unread" : ""
                    }`}
                    onClick={() => handleOpenDetail(item)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Feedback from ${item.user_name || item.user_email}`}
                  >
                    {/* Top Row: User Meta + Rating & Status */}
                    <div className="admin-card-top-row">
                      <div className="admin-card-user-meta">
                        <div
                          className="admin-card-avatar"
                          style={{ background: avatarColor }}
                        >
                          {initial}
                        </div>
                        <div className="admin-card-user-details">
                          <div className="admin-card-user-name-line">
                            <span className="admin-card-user-name">
                              {item.user_name || item.user_email || "Anonymous Client"}
                            </span>
                            {item.user_role && (
                              <span className="admin-card-role-badge">
                                {item.user_role}
                              </span>
                            )}
                          </div>
                          <div className="admin-card-user-subline">
                            <span className="admin-card-subline-item">
                              <Mail size={12} /> {item.user_email || "No email"}
                            </span>
                            {item.user_organization && (
                              <span className="admin-card-subline-item">
                                <Building2 size={12} /> {item.user_organization}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="admin-card-right-meta">
                        <span className={`feedback-rating-pill ${item.rating}`}>
                          {getMiniRatingIcon(item.rating)}
                          {item.rating}
                        </span>
                        <span className={`admin-card-status-pill ${item.status}`}>
                          {item.status}
                        </span>
                        <span className="admin-card-time">
                          <Clock size={12} /> {formatTimeAgo(item.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Suggestions Content Preview */}
                    {item.suggestions && (
                      <div className="admin-card-quote-box">
                        "{item.suggestions}"
                      </div>
                    )}

                    {/* Footer hint */}
                    <div className="admin-card-footer">
                      <span>Click to review details & take action</span>
                      <ChevronRight size={13} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Feedback Detail Modal */}
      <FeedbackDetailModal
        feedback={activeDetailFeedback}
        onClose={() => setActiveDetailFeedback(null)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
}
